import type { AttachmentResponse } from "@/types/notes";
import type { TicketData, UpdateTicketRequest, TicketAttachment } from "@telegraphio/bapi-types";

/**
 * Maps a ticket note attachment from the API response to the internal AttachmentResponse type.
 */
export function mapNoteAttachment(attachment: TicketAttachment, userId: string): AttachmentResponse {
  return {
    id: attachment.id,
    alt_text: attachment.alt_text ?? "",
    attachment_id: attachment.id,
    created_ts: attachment.created_ts,
    download_url: attachment.download_url ?? "",
    extension: attachment.extension ?? "",
    filename: attachment.filename ?? "",
    filesize: attachment.filesize ?? 0,
    mime_type: attachment.mime_type ?? "",
    normalized_url: attachment.normalized_url ?? "",
    status: Boolean(attachment.status),
    thumbnail_status: attachment.thumbnail_status ?? 0,
    thumbnail_url: attachment.thumbnail_url ?? "",
    updated_ts: attachment.updated_ts,
    user_id: userId,
    version: attachment.version ?? 0,
  };
}

/**
 * Builds an UpdateTicketRequest from existing TicketData with optional overrides.
 * Handles the common pattern of preserving all ticket fields while updating specific ones.
 */
export function buildUpdateTicketRequest(
  ticket: TicketData,
  overrides: Partial<UpdateTicketRequest>,
): UpdateTicketRequest {
  const ticketAssets: UpdateTicketRequest["ticket_assets"] = [];

  for (const equip of ticket.ticket_assets ?? []) {
    if (equip.equipment_id !== undefined) {
      const mappedGroups = equip.reporting_group_name_map
        ?.map((group) => group.name)
        .filter((name): name is string => !!name);
      const reporting_groups = mappedGroups?.length ? mappedGroups : null;

      ticketAssets.push({
        equipment_id: equip.equipment_id ?? null,
        waybill_id: equip.waybill_id ?? null,
        reporting_groups,
      });
    }
  }

  return {
    asset_ids: ticket.asset_ids ?? [],
    assignees: ticket.assignees?.map((assignee) => assignee.user_id) ?? [],
    description: ticket.description ?? "",
    equipment_ids: [],
    followers: ticket.followers?.map((follower) => follower.user_id) ?? [],
    loaded_empty: ticket.loaded_empty ?? "L",
    name: ticket.name ?? "",
    priority: ticket.priority ?? "",
    railroads: ticket.railroads ?? [],
    reporting_groups: ticket.reporting_groups ?? [],
    state: ticket.state ?? null,
    tags: ticket.tags ?? [],
    ticket_assets: ticketAssets,
    waybill_ids: ticket.waybill_ids ?? [],
    ...overrides,
  };
}
